import React from 'react'
import { Edit, Clock, CheckCircle2, X } from 'lucide-react'
import { sastToday } from '@/components/admin/lifecycle/spellCoverage'

// Template status badge configurations
export const STATUS_CONFIGS: Record<string, { icon: React.ReactNode; color: string; bgColor: string; label: string }> = {
  'draft': { icon: React.createElement(Edit, { className: "h-3 w-3" }), color: 'text-gray-700', bgColor: 'bg-gray-100', label: 'Draft' },
  'pending_approval': { icon: React.createElement(Clock, { className: "h-3 w-3" }), color: 'text-amber-700', bgColor: 'bg-amber-100', label: 'Pending' },
  'approved': { icon: React.createElement(CheckCircle2, { className: "h-3 w-3" }), color: 'text-green-700', bgColor: 'bg-green-100', label: 'Approved' },
  'rejected': { icon: React.createElement(X, { className: "h-3 w-3" }), color: 'text-red-700', bgColor: 'bg-red-100', label: 'Rejected' },
}

export const TEMPLATE_TYPE_CONFIGS: Record<string, { color: string; bgColor: string }> = {
  'role_based': { color: 'text-blue-700', bgColor: 'bg-blue-100' },
  'institutional': { color: 'text-purple-700', bgColor: 'bg-purple-100' },
  'generic': { color: 'text-teal-700', bgColor: 'bg-teal-100' },
  'manager_created': { color: 'text-indigo-700', bgColor: 'bg-indigo-100' },
  'department': { color: 'text-cyan-700', bgColor: 'bg-cyan-100' },
  'functional': { color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
}

export type ViewMode = 'grid' | 'list' | 'compact'

// Import types for CSV parsing
export interface ImportedKPI {
  name: string
  description: string
  unit: string
  target_value: string
  frequency: string
  measurement_type?: string  // numeric, percentage, rating, binary
  progress_direction?: string  // higher_is_better, lower_is_better
}

export interface ImportedObjective {
  pillar_name: string
  strategic_objective_name: string
  l3_operational_initiative_name?: string
  kpa_name: string
  // New L1-L3 code-based fields
  l1_strategic_objective_code?: string
  l2_strategic_initiative_code?: string
  l3_operational_initiative_code?: string
  kpa_code?: string
  title: string
  description: string
  start_date: string
  end_date: string
  kpis: ImportedKPI[]
}

export interface ImportedTemplate {
  name: string
  description: string
  template_type: string
  role_type: string
  objectives: ImportedObjective[]
}

export interface ImportValidationError {
  row: number
  field: string
  message: string
  severity: 'error' | 'warning'
}

export interface ImportPreviewData {
  templates: ImportedTemplate[]
  errors: ImportValidationError[]
  warnings: ImportValidationError[]
  totalRows: number
  validRows: number
}

// CSV Parsing utilities
export const parseCSVLine = (line: string): string[] => {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    const nextChar = line[i + 1]

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"'
        i++ // Skip next quote
      } else {
        inQuotes = !inQuotes
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  result.push(current.trim())
  return result
}

export const parseCSV = (content: string): string[][] => {
  const lines = content.split(/\r?\n/).filter(line => line.trim())
  return lines.map(parseCSVLine)
}

// Strategic framework lookup maps for code validation
export const L1_STRATEGIC_OBJECTIVES: Record<string, { id: number; name: string; display_name: string }> = {
  '1': { id: 5, name: 'IMPACT', display_name: 'Impact' },
  '2': { id: 6, name: 'CULTURE', display_name: 'Culture' },
  '3': { id: 7, name: 'QUALITY', display_name: 'Quality' },
  '4': { id: 8, name: 'SCALE', display_name: 'Scale' },
}

export const L2_STRATEGIC_INITIATIVES: Record<string, { id: number; name: string; parent_code: string }> = {
  '1.1': { id: 9, name: 'Sustainability and Purpose', parent_code: '1' },
  '1.2': { id: 10, name: 'Ecosystem Influence & Societal Impact', parent_code: '1' },
  '2.1': { id: 11, name: 'Workforce Agility, Capability & Belonging', parent_code: '2' },
  '2.2': { id: 12, name: 'Stakeholder Inclusion & Student Belonging', parent_code: '2' },
  '3.1': { id: 13, name: 'Purposeful Excellence', parent_code: '3' },
  '3.2': { id: 14, name: 'Future-Fit Innovation', parent_code: '3' },
  '4.1': { id: 15, name: 'Strengthen & Optimise Core', parent_code: '4' },
  '4.2': { id: 16, name: 'Expand Growth & Access', parent_code: '4' },
}

export const L3_OPERATIONAL_INITIATIVES: Record<string, { id: number; name: string; parent_code: string }> = {
  '1.1.1': { id: 17, name: 'Optimise Business Models & Value Creation', parent_code: '1.1' },
  '1.1.2': { id: 18, name: 'Strengthen business resilience and governance', parent_code: '1.1' },
  '1.2.1': { id: 19, name: 'Grow Eduvos Stakeholder ecosystem', parent_code: '1.2' },
  '1.2.2': { id: 20, name: 'Advance Responsible Futures', parent_code: '1.2' },
  '2.1.1': { id: 21, name: 'Cultivate a purpose and values driven culture', parent_code: '2.1' },
  '2.1.2': { id: 22, name: 'Develop a competent and future-fit organisational talent profile', parent_code: '2.1' },
  '2.2.1': { id: 23, name: 'Increase student belonging (current students)', parent_code: '2.2' },
  '2.2.2': { id: 24, name: 'Strengthen external stakeholder relationships', parent_code: '2.2' },
  '3.1.1': { id: 25, name: 'Improve quality of Learning, Teaching and Assessment', parent_code: '3.1' },
  '3.1.2': { id: 26, name: 'Deliver a consistent, high-quality service level and customer experience', parent_code: '3.1' },
  '3.2.1': { id: 27, name: 'Drive innovation across the organisation', parent_code: '3.2' },
  '3.2.2': { id: 28, name: 'Ensure future-fit systems and processes', parent_code: '3.2' },
  '4.1.1': { id: 29, name: 'Improve Operational Efficiency', parent_code: '4.1' },
  '4.1.2': { id: 30, name: 'Ensure resilient, scalable, modern physical and digital infrastructure', parent_code: '4.1' },
  '4.2.1': { id: 31, name: 'Expand Markets', parent_code: '4.2' },
  '4.2.2': { id: 32, name: 'Expand Product Offerings', parent_code: '4.2' },
}

export const KPA_LOOKUP: Record<string, { id: number; display_name: string }> = {
  'operating-financial': { id: 1, display_name: 'Operating and Financial Results' },
  'internal-business-processes': { id: 2, display_name: 'Internal Business Processes and Enablement' },
  'culture-people': { id: 3, display_name: 'Culture and People' },
  'customer-stakeholder': { id: 4, display_name: 'Customer and Stakeholder Relationships' },
}

// Helper to convert Excel date serial (from L3 codes like 3.1.1) back to code format
// Excel interprets "3.1.1" as time 3:01:01 → date serial → "1899-12-30 03:01:01"
export const parseExcelL3Code = (value: string): string => {
  if (!value) return ''

  // If already in code format (e.g., "3.1.1"), return as-is
  if (/^\d+\.\d+\.\d+$/.test(value.trim())) {
    return value.trim()
  }

  // Check for Excel date format like "1899-12-30 03:01:01" or similar
  const dateTimeMatch = value.match(/(\d{4}-\d{2}-\d{2})\s+(\d{1,2}):(\d{2}):(\d{2})/)
  if (dateTimeMatch) {
    const hours = parseInt(dateTimeMatch[2], 10)
    const minutes = parseInt(dateTimeMatch[3], 10)
    const seconds = parseInt(dateTimeMatch[4], 10)
    // Convert back: 3:01:01 → "3.1.1"
    return `${hours}.${minutes}.${seconds}`
  }

  // Check for just time format "03:01:01"
  const timeMatch = value.match(/^(\d{1,2}):(\d{2}):(\d{2})$/)
  if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10)
    const minutes = parseInt(timeMatch[2], 10)
    const seconds = parseInt(timeMatch[3], 10)
    return `${hours}.${minutes}.${seconds}`
  }

  return value.trim()
}

// Helper to normalize date fields from Excel format
export const parseExcelDate = (value: string | number): string => {
  if (value === null || value === undefined || value === '') return ''

  // Handle Excel serial date numbers (xlsx library often returns these)
  if (typeof value === 'number' || /^\d+(\.\d+)?$/.test(String(value).trim())) {
    const serial = typeof value === 'number' ? value : parseFloat(String(value).trim())
    if (serial > 1 && serial < 100000) {
      // Excel epoch is 1900-01-01, but Excel incorrectly counts 1900 as leap year
      // Days since 1899-12-30 (Excel's effective epoch)
      const excelEpoch = new Date(1899, 11, 30)
      const date = new Date(excelEpoch.getTime() + serial * 24 * 60 * 60 * 1000)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }

  const trimmed = String(value).trim()

  // Already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed
  }

  // Excel datetime format "2025-01-01 00:00:00"
  const dateTimeMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})\s+\d{2}:\d{2}:\d{2}$/)
  if (dateTimeMatch) {
    return dateTimeMatch[1]
  }

  // DD-MMM-YY format (01-Jan-25, 31-Dec-25)
  const monthMap: Record<string, string> = {
    'jan': '01', 'feb': '02', 'mar': '03', 'apr': '04',
    'may': '05', 'jun': '06', 'jul': '07', 'aug': '08',
    'sep': '09', 'oct': '10', 'nov': '11', 'dec': '12'
  }
  const ddMmmYyMatch = trimmed.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2})$/)
  if (ddMmmYyMatch) {
    const day = ddMmmYyMatch[1].padStart(2, '0')
    const month = monthMap[ddMmmYyMatch[2].toLowerCase()]
    const year = parseInt(ddMmmYyMatch[3], 10)
    // Assume 2000s for years 00-99
    const fullYear = year < 50 ? 2000 + year : 1900 + year
    if (month) {
      return `${fullYear}-${month}-${day}`
    }
  }

  // DD-MMM-YYYY format (01-Jan-2025)
  const ddMmmYyyyMatch = trimmed.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/)
  if (ddMmmYyyyMatch) {
    const day = ddMmmYyyyMatch[1].padStart(2, '0')
    const month = monthMap[ddMmmYyyyMatch[2].toLowerCase()]
    const fullYear = ddMmmYyyyMatch[3]
    if (month) {
      return `${fullYear}-${month}-${day}`
    }
  }

  // DD/MM/YYYY format
  const ddmmyyyyMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (ddmmyyyyMatch) {
    const day = ddmmyyyyMatch[1].padStart(2, '0')
    const month = ddmmyyyyMatch[2].padStart(2, '0')
    const year = ddmmyyyyMatch[3]
    return `${year}-${month}-${day}`
  }

  // MM/DD/YYYY format (US)
  const mmddyyyyMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (mmddyyyyMatch) {
    const month = mmddyyyyMatch[1].padStart(2, '0')
    const day = mmddyyyyMatch[2].padStart(2, '0')
    const year = mmddyyyyMatch[3]
    // If month > 12, it's likely DD/MM/YYYY
    if (parseInt(month, 10) <= 12) {
      return `${year}-${month}-${day}`
    }
  }

  return trimmed
}

export const validateImportRow = (
  row: string[],
  headers: string[],
  rowIndex: number
): ImportValidationError[] => {
  const errors: ImportValidationError[] = []
  const getValue = (field: string) => row[headers.indexOf(field)] || ''

  // Check if using new code-based format or legacy name-based format
  const hasCodeFormat = headers.includes('l1_strategic_objective_code') || headers.includes('kpa_code')

  // Required fields validation - adapt based on format
  const requiredFields = hasCodeFormat
    ? ['template_name', 'objective_title', 'kpa_code']
    : ['template_name', 'objective_title', 'kpa_name']

  requiredFields.forEach(field => {
    if (!getValue(field)) {
      errors.push({
        row: rowIndex + 2, // +2 for header and 0-index
        field,
        message: `${field.replace(/_/g, ' ')} is required`,
        severity: 'error'
      })
    }
  })

  // Date validation - normalize Excel datetime format first
  const dateFields = ['start_date', 'end_date']
  dateFields.forEach(field => {
    const rawValue = getValue(field)
    const value = parseExcelDate(rawValue)
    if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      errors.push({
        row: rowIndex + 2,
        field,
        message: `Invalid date format for ${field.replace(/_/g, ' ')}. Expected YYYY-MM-DD`,
        severity: 'error'
      })
    }
  })

  // Template type validation
  const templateType = getValue('template_type')
  if (templateType && !['role_based', 'department', 'functional', 'institutional', 'generic'].includes(templateType)) {
    errors.push({
      row: rowIndex + 2,
      field: 'template_type',
      message: `Invalid template type. Must be: role_based, department, functional, institutional, or generic`,
      severity: 'warning'
    })
  }

  // KPI frequency validation. Accepts the app's canonical vocabulary
  // ('bi-annually'/'annually' — see ImprovedObjectiveWizard's frequency type
  // and RoleKPI rows in the DB) alongside the legacy import aliases
  // ('semester'/'annual') so exported objectives round-trip warning-free.
  const frequency = getValue('kpi_frequency')
  const VALID_FREQUENCIES = [
    'daily', 'weekly', 'monthly', 'quarterly',
    'bi-annually', 'semester',   // twice a year (app canonical / legacy alias)
    'annually', 'annual',        // once a year (app canonical / legacy alias)
  ]
  if (frequency && !VALID_FREQUENCIES.includes(frequency)) {
    errors.push({
      row: rowIndex + 2,
      field: 'kpi_frequency',
      message: `Invalid KPI frequency '${frequency}'. Recommended: monthly, quarterly, bi-annually, or annually`,
      severity: 'warning'
    })
  }

  // Code-based validation for L1-L3
  if (hasCodeFormat) {
    const l1Code = getValue('l1_strategic_objective_code')
    if (l1Code && !L1_STRATEGIC_OBJECTIVES[l1Code]) {
      errors.push({
        row: rowIndex + 2,
        field: 'l1_strategic_objective_code',
        message: `Invalid L1 code: ${l1Code}. Valid codes: 1 (Impact), 2 (Culture), 3 (Quality), 4 (Scale)`,
        severity: 'error'
      })
    }

    const l2Code = getValue('l2_strategic_initiative_code')
    if (l2Code && !L2_STRATEGIC_INITIATIVES[l2Code]) {
      errors.push({
        row: rowIndex + 2,
        field: 'l2_strategic_initiative_code',
        message: `Invalid L2 code: ${l2Code}. Check the lookups template for valid codes.`,
        severity: 'error'
      })
    } else if (l2Code && l1Code) {
      const l2 = L2_STRATEGIC_INITIATIVES[l2Code]
      if (l2 && l2.parent_code !== l1Code) {
        errors.push({
          row: rowIndex + 2,
          field: 'l2_strategic_initiative_code',
          message: `L2 code ${l2Code} does not belong to L1 code ${l1Code}`,
          severity: 'warning'
        })
      }
    }

    const rawL3Code = getValue('l3_operational_initiative_code')
    const l3Code = parseExcelL3Code(rawL3Code)
    if (l3Code && !L3_OPERATIONAL_INITIATIVES[l3Code]) {
      errors.push({
        row: rowIndex + 2,
        field: 'l3_operational_initiative_code',
        message: `Invalid L3 code: ${l3Code} (raw: ${rawL3Code}). Check the lookups template for valid codes.`,
        severity: 'error'
      })
    } else if (l3Code && l2Code) {
      const l3 = L3_OPERATIONAL_INITIATIVES[l3Code]
      if (l3 && l3.parent_code !== l2Code) {
        errors.push({
          row: rowIndex + 2,
          field: 'l3_operational_initiative_code',
          message: `L3 code ${l3Code} does not belong to L2 code ${l2Code}`,
          severity: 'warning'
        })
      }
    }

    const kpaCode = getValue('kpa_code')
    if (kpaCode && !KPA_LOOKUP[kpaCode]) {
      errors.push({
        row: rowIndex + 2,
        field: 'kpa_code',
        message: `Invalid KPA code: ${kpaCode}. Valid codes: operating-financial, internal-business-processes, culture-people, customer-stakeholder`,
        severity: 'error'
      })
    }
  } else {
    // Legacy name-based validation (L1)
    const pillarName = getValue('pillar_name')
    if (pillarName && !['SCALE', 'QUALITY', 'CULTURE', 'IMPACT'].includes(pillarName.toUpperCase())) {
      errors.push({
        row: rowIndex + 2,
        field: 'pillar_name',
        message: `Unknown pillar: ${pillarName}. Expected: SCALE, QUALITY, CULTURE, or IMPACT`,
        severity: 'warning'
      })
    }
  }

  return errors
}

export const processImportData = (rows: string[][], headers: string[]): ImportPreviewData => {
  const templates = new Map<string, ImportedTemplate>()
  const allErrors: ImportValidationError[] = []
  const allWarnings: ImportValidationError[] = []
  let validRows = 0

  // Check if using new code-based format
  const hasCodeFormat = headers.includes('l1_strategic_objective_code') || headers.includes('kpa_code')

  rows.forEach((row, index) => {
    const getValue = (field: string) => row[headers.indexOf(field)] || ''

    // Validate row
    const rowErrors = validateImportRow(row, headers, index)
    const errors = rowErrors.filter(e => e.severity === 'error')
    const warnings = rowErrors.filter(e => e.severity === 'warning')

    allErrors.push(...errors)
    allWarnings.push(...warnings)

    // Skip rows with critical errors
    if (errors.length > 0) return

    validRows++

    const templateName = getValue('template_name')
    const objectiveTitle = getValue('objective_title')

    // Get or create template
    if (!templates.has(templateName)) {
      templates.set(templateName, {
        name: templateName,
        description: getValue('template_description') || `Imported template: ${templateName}`,
        template_type: getValue('template_type') || 'role_based',
        role_type: getValue('role_title') || getValue('role_type') || '',  // Support both role_title (new) and role_type (legacy)
        objectives: []
      })
    }

    const template = templates.get(templateName)!

    // Resolve codes to names if using code-based format
    let pillarName = getValue('pillar_name')
    let strategicObjectiveName = getValue('strategic_objective_name')
    let l3OperationalInitiativeName = ''
    let kpaName = getValue('kpa_name')
    let l1Code = ''
    let l2Code = ''
    let l3Code = ''
    let kpaCode = ''

    if (hasCodeFormat) {
      // Get raw values and normalize (handle Excel date serial conversion for L3)
      const rawL1Code = getValue('l1_strategic_objective_code')
      const rawL2Code = getValue('l2_strategic_initiative_code')
      const rawL3Code = getValue('l3_operational_initiative_code')

      l1Code = String(rawL1Code).trim()
      l2Code = String(rawL2Code).trim()
      l3Code = parseExcelL3Code(rawL3Code)
      kpaCode = getValue('kpa_code')

      // Resolve L1 code to pillar name
      if (l1Code && L1_STRATEGIC_OBJECTIVES[l1Code]) {
        pillarName = L1_STRATEGIC_OBJECTIVES[l1Code].name
      }

      // Resolve L2 code to strategic objective name
      if (l2Code && L2_STRATEGIC_INITIATIVES[l2Code]) {
        strategicObjectiveName = L2_STRATEGIC_INITIATIVES[l2Code].name
      }

      // Resolve L3 code to operational initiative name
      if (l3Code && L3_OPERATIONAL_INITIATIVES[l3Code]) {
        l3OperationalInitiativeName = L3_OPERATIONAL_INITIATIVES[l3Code].name
      }

      // Resolve KPA code to display name
      if (kpaCode && KPA_LOOKUP[kpaCode]) {
        kpaName = KPA_LOOKUP[kpaCode].display_name
      }
    }

    // Find or create objective
    let objective = template.objectives.find(o => o.title === objectiveTitle)
    if (!objective) {
      // Parse dates - handle Excel datetime format. The defaults are South
      // African days: the UTC day is still yesterday until 02:00, and 31 Dec
      // at local midnight is 30 Dec in UTC.
      const today = sastToday()
      const startDate = parseExcelDate(getValue('start_date')) || today
      const endDate = parseExcelDate(getValue('end_date')) || `${today.slice(0, 4)}-12-31`

      objective = {
        pillar_name: pillarName,
        strategic_objective_name: strategicObjectiveName,
        l3_operational_initiative_name: l3OperationalInitiativeName || undefined,
        kpa_name: kpaName,
        // Store codes for API submission
        l1_strategic_objective_code: l1Code || undefined,
        l2_strategic_initiative_code: l2Code || undefined,
        l3_operational_initiative_code: l3Code || undefined,
        kpa_code: kpaCode || undefined,
        title: objectiveTitle,
        description: getValue('objective_description') || '',
        start_date: startDate,
        end_date: endDate,
        kpis: []
      }
      template.objectives.push(objective)
    }

    // Add KPI if present
    const kpiName2 = getValue('kpi_name')
    if (kpiName2 && !objective.kpis.find(k => k.name === kpiName2)) {
      // Get measurement type (new field) or infer from legacy kpi_unit
      const measurementType = getValue('kpi_measurement_type') || getValue('kpi_unit') || 'numeric'

      // Determine unit based on measurement type
      let unit = 'count'
      if (measurementType === 'percentage') {
        unit = '%'
      } else if (measurementType === 'rating') {
        unit = 'status'
      } else if (measurementType === 'binary') {
        unit = 'achieved'
      } else {
        // For numeric, use the kpi_unit column or default
        unit = getValue('kpi_unit') || 'count'
      }

      // Get progress direction (new field) or default based on unit/name
      let progressDirection = getValue('kpi_progress_direction') || 'higher_is_better'
      // Auto-infer if not specified
      if (!getValue('kpi_progress_direction')) {
        const kpiNameLower = kpiName2.toLowerCase()
        if (unit.includes('day') || unit.includes('hour') ||
            kpiNameLower.includes('turnaround') || kpiNameLower.includes('waiting') ||
            kpiNameLower.includes('defect') || kpiNameLower.includes('error')) {
          progressDirection = 'lower_is_better'
        }
      }

      objective.kpis.push({
        name: kpiName2,
        description: getValue('kpi_description') || '',
        unit: unit,
        target_value: getValue('kpi_target_value') || '0',
        frequency: getValue('kpi_frequency') || 'quarterly',
        measurement_type: measurementType,
        progress_direction: progressDirection
      })
    }
  })

  return {
    templates: Array.from(templates.values()),
    errors: allErrors,
    warnings: allWarnings,
    totalRows: rows.length,
    validRows
  }
}
